import React, { useState, useEffect } from 'react';
import './Skills.css';
import LogoLoop from './LogoLoop';

const Skills = () => {
    const [screenSize, setScreenSize] = useState('desktop');

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            if (width <= 480) {
                setScreenSize('smallMobile');
            } else if (width <= 768) {
                setScreenSize('mobile');
            } else if (width <= 1024) {
                setScreenSize('tablet');
            } else {
                setScreenSize('desktop');
            }
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Responsive carousel settings
    const getCarouselSettings = () => {
        switch (screenSize) {
            case 'smallMobile':
                return { logoHeight: 32, gap: 40, speed: 35 };
            case 'mobile':
                return { logoHeight: 36, gap: 45, speed: 40 };
            case 'tablet':
                return { logoHeight: 40, gap: 50, speed: 45 };
            default:
                return { logoHeight: 45, gap: 60, speed: 50 };
        }
    };

    const { logoHeight, gap, speed } = getCarouselSettings();

    const aiSkills = [
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg', alt: 'Python' },
        { src: 'https://www.vectorlogo.zone/logos/opencv/opencv-icon.svg', alt: 'OpenCV' },
        { src: 'https://www.khronos.org/assets/images/api_logos/opencl.svg', alt: 'OpenCL' },
        { src: 'https://www.vectorlogo.zone/logos/pytorch/pytorch-icon.svg', alt: 'PyTorch' },
        { src: 'https://www.vectorlogo.zone/logos/tensorflow/tensorflow-icon.svg', alt: 'TensorFlow' },
        { src: 'https://upload.wikimedia.org/wikipedia/commons/0/05/Scikit_learn_logo_small.svg', alt: 'scikit-learn' },
        { src: 'https://keras.io/img/logo.png', alt: 'Keras' },
        { src: 'https://huggingface.co/front/assets/huggingface_logo.svg', alt: 'Hugging Face' },
        { src: require('../assets/images/googlevision ai.png'), alt: 'Vision AI' },
        { src: 'https://www.vectorlogo.zone/logos/nvidia/nvidia-icon.svg', alt: 'NVIDIA CUDA' },
    ];

    const softwareSkills = [
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg', alt: 'Kotlin' },
        { src: require('../assets/images/jetpackcompose.png'), alt: 'Jetpack Compose' },
        { src: 'https://storage.googleapis.com/cms-storage-bucket/6a07d8a62f4308d2b854.svg', alt: 'Flutter' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/dart/dart-original.svg', alt: 'Dart' },
        { src: 'https://www.vectorlogo.zone/logos/firebase/firebase-icon.svg', alt: 'Firebase' },
        { src: require('../assets/images/supabase.png'), alt: 'Supabase' },
        { src: 'https://www.vectorlogo.zone/logos/google_cloud/google_cloud-icon.svg', alt: 'Google APIs' },
        { src: require('../assets/images/Openstreetmap.png'), alt: 'OpenStreetMap' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg', alt: 'React' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/nodejs/nodejs-original.svg', alt: 'Node.js' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/nextjs/nextjs-original.svg', alt: 'Next.js' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg', alt: 'TypeScript' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg', alt: 'JavaScript' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/mongodb/mongodb-original.svg', alt: 'MongoDB' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/postgresql/postgresql-original.svg', alt: 'PostgreSQL' },
    ];

    const toolsSkills = [
        { src: require('../assets/images/androidstudio.png'), alt: 'Android Studio' },
        { src: 'https://www.vectorlogo.zone/logos/visualstudio_code/visualstudio_code-icon.svg', alt: 'VS Code' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/intellij/intellij-original.svg', alt: 'IntelliJ IDEA' },
        { src: 'https://www.vectorlogo.zone/logos/git-scm/git-scm-icon.svg', alt: 'Git' },
        { src: require('../assets/images/github.png'), alt: 'GitHub' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/docker/docker-original.svg', alt: 'Docker' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/kubernetes/kubernetes-plain.svg', alt: 'Kubernetes' },
        { src: 'https://www.vectorlogo.zone/logos/getpostman/getpostman-icon.svg', alt: 'Postman' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/amazonwebservices/amazonwebservices-original-wordmark.svg', alt: 'AWS' },
        { src: 'https://www.vectorlogo.zone/logos/google_cloud/google_cloud-icon.svg', alt: 'Google Cloud' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/nginx/nginx-original.svg', alt: 'Nginx' },
        { src: 'https://www.vectorlogo.zone/logos/gnu_bash/gnu_bash-icon.svg', alt: 'Bash' },
        { src: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/linux/linux-original.svg', alt: 'Linux' },
        { src: 'https://www.vectorlogo.zone/logos/jenkins/jenkins-icon.svg', alt: 'Jenkins' },
    ];

    return (
        <section id="skills" className="section">
            <div className="skills-container">
                <div className="about-section">
                    <h2>About Me</h2>
                    <p>
                        I'm the Founding Engineer of Scoutrex, working with international clients to build scalable, high-impact technology solutions. I specialize in taking products from concept to deployment, ensuring a seamless journey from idea to production.
                    </p>
                    <p>
                        With experience across AI, Computer Vision, and Software Engineering, I design and develop robust systems that solve real-world problems. From architecture and backend systems to intelligent applications and optimization, I focus on building technology that is efficient, scalable, and built to perform.
                    </p>
                    <p>
                        As a Computer Science professional, I'm driven by innovation, execution excellence, and creating solutions that don't just work — they stand out.
                    </p>
                </div>

                <div className="skills-heading-wrapper">
                    <h2>Skills</h2>
                </div>
                <div className="skills-category-heading">
                    <h3>Computer Vision & AI</h3>
                </div>
                <div className="skills-logoloop-container">
                    <LogoLoop
                        logos={aiSkills}
                        speed={speed}
                        direction="left"
                        logoHeight={logoHeight}
                        gap={gap}
                        hoverSpeed={0}
                        scaleOnHover={true}
                        fadeOut={true}
                        fadeOutColor="transparent"
                        ariaLabel="AI & Computer Vision Technologies"
                    />
                </div>
                <div className="skills-category-heading">
                    <h3>Software Engineering</h3>
                </div>
                <div className="skills-logoloop-container">
                    <LogoLoop
                        logos={softwareSkills}
                        speed={speed}
                        direction="right"
                        logoHeight={logoHeight}
                        gap={gap}
                        hoverSpeed={0}
                        scaleOnHover={true}
                        fadeOut={true}
                        fadeOutColor="transparent"
                        ariaLabel="Software Engineering Technologies"
                    />
                </div>
                <div className="skills-category-heading">
                    <h3>Tools and Development</h3>
                </div>
                <div className="skills-logoloop-container">
                    <LogoLoop
                        logos={toolsSkills}
                        speed={speed}
                        direction="left"
                        logoHeight={logoHeight}
                        gap={gap}
                        hoverSpeed={0}
                        scaleOnHover={true}
                        fadeOut={true}
                        fadeOutColor="transparent"
                        ariaLabel="Tools and Development"
                    />
                </div>
            </div>
        </section>
    );
};

export default Skills;
